import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { Home, Search, MessageCircle, Bell, User, Settings, LogOut, Plus, Radio, Crown, Lock, ShoppingBag, Wallet, Package, Headphones, Phone, Video, PhoneOff, Menu, X, FileText, HelpCircle, Shield, Globe, Info, Coins, Wand2, Moon, Sun, Users, Gamepad2, CalendarCheck, Bot, Film, BarChart3, Sparkles, Compass, CalendarClock } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { getInitials } from '../../lib/utils';
import CreatePostModal from '../feed/CreatePostModal';
import GlobalSearchBar from '../search/GlobalSearchBar';
import AIBotModal from '../ai/AIBotModal';
import IncomingCallScreen from '../calls/IncomingCallScreen';
import useIncomingCallSocket from '../../hooks/useIncomingCallSocket';
import { NotificationPrompt } from '../../hooks/usePushNotifications';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0c49a145-2431-460e-8df4-1bea13020ea4/artifacts/j4k88xee_file_00000000c9b871f8a2879288a4f57131.png";

export default function AppLayout() {
  const { user, token, language, logout, theme, toggleTheme } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const tr = t(language);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showAIBot, setShowAIBot] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  // Robust incoming-call socket (with reconnect, ringtone, vibrate, missed-call timeout)
  const { incomingCall, acceptCall, declineCall } = useIncomingCallSocket({
    userId: user?.id,
    token,
    onCallEnded: (call) => {
      if (call?.status === 'missed') {
        // Refresh unread badge so missed-call notification surfaces immediately
        fetchUnreadCount();
      }
    },
    onNotification: () => {
      // Real-time: a new notification just arrived over WS — bump the badge
      // immediately instead of waiting for the 30s poll.
      setUnreadNotifications((c) => c + 1);
    },
  });

  const answerCall = async () => {
    const callData = await acceptCall();
    if (callData) {
      navigate(`/call/${callData.id}`, { state: { call: callData } });
    }
  };
  
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);
  
  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadNotifications(response.data.count);
    } catch (error) {
      console.error('Failed to fetch unread count');
    }
  };
  
  const navItems = [
    { path: '/', icon: Home, label: tr('home') },
    { path: '/explore', icon: Search, label: tr('explore') },
    { path: '/reels', icon: Film, label: 'Reels' },
    { path: '/live', icon: Radio, label: 'Live' },
    { path: '/chat', icon: MessageCircle, label: tr('chat') },
    { path: '/notifications', icon: Bell, label: tr('notifications'), badge: unreadNotifications },
  ];

  const extraNavItems = [
    { path: '/discover', icon: Compass, label: 'Discover' },
    { path: '/scheduled-posts', icon: CalendarClock, label: 'Scheduled' },
    { path: '/communities', icon: Users, label: 'Communities' },
    { path: '/domino', icon: Gamepad2, label: 'Domino' },
    { path: '/appointments', icon: CalendarCheck, label: tr('appointments') },
    { path: '/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/coins', icon: Coins, label: 'Coins' },
    { path: '/studio', icon: Wand2, label: 'AI Studio' },
    { path: '/secret-chats', icon: Lock, label: 'Secret Chat' },
    { path: '/creator', icon: Crown, label: 'Creator' },
    { path: '/marketplace', icon: ShoppingBag, label: 'Marketplace' },
    { path: '/marketplace/orders', icon: Package, label: 'Orders' },
    { path: '/withdrawals', icon: Wallet, label: 'Withdraw' },
    { path: '/support', icon: Headphones, label: 'Support' },
  ];
  
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };
  
  return (
    <div className="min-h-screen bg-background" data-testid="app-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-full w-64 border-r border-border/50 bg-background/50 backdrop-blur-xl flex-col p-6 z-40">
        {/* Logo */}
        <div className="mb-4 flex items-center gap-3">
          <img src={LOGO_URL} alt="FityH" className="w-12 h-12 object-contain rounded-xl" />
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-500 to-violet-500 bg-clip-text text-transparent tracking-tight">FityH</h1>
            <p className="text-xs text-muted-foreground">{tr('tagline')}</p>
          </div>
        </div>
        
        {/* Desktop Search Bar */}
        <div className="mb-4">
          <GlobalSearchBar className="w-full" />
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto">
          {navItems.map(({ path, icon: Icon, label, badge }) => (
            <NavLink
              key={path}
              to={path}
              data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
              className={`flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-200 ${
                isActive(path)
                  ? 'bg-gradient-to-r from-pink-500 to-violet-500 text-white shadow-lg shadow-pink-500/20'
                  : 'hover:bg-secondary/80 text-foreground hover:translate-x-1'
              }`}
            >
              <div className="relative">
                <Icon className="w-6 h-6" />
                {badge > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-pink-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                    {badge > 9 ? '9+' : badge}
                  </span>
                )}
              </div>
              <span className="font-medium">{label}</span>
            </NavLink>
          ))}
          
          {/* Create Post Button */}
          <Button
            onClick={() => setShowCreatePost(true)}
            className="w-full mt-4 rounded-full font-bold bg-gradient-to-r from-pink-500 to-violet-500 hover:from-pink-600 hover:to-violet-600 shadow-lg shadow-pink-500/25 hover:shadow-pink-500/40 transition-all duration-200 hover:scale-[1.02]"
            data-testid="create-post-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            {tr('newPost')}
          </Button>
          
          {/* AI Bot Button */}
          <Button
            onClick={() => setShowAIBot(true)}
            variant="outline"
            className="w-full mt-2 rounded-full font-bold border-pink-500/50 text-pink-500 hover:bg-pink-500/10 hover:border-pink-500 transition-all duration-200"
            data-testid="ai-bot-btn"
          >
            <Bot className="w-5 h-5 mr-2" />
            FityBot AI
          </Button>

          {/* Divider */}
          <div className="border-t border-border/50 my-4" />

          {/* Extra Nav Items */}
          {extraNavItems.map(({ path, icon: Icon, label }) => (
            <NavLink
              key={path}
              to={path}
              data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
              className={`flex items-center gap-4 px-4 py-3 rounded-2xl transition-colors ${
                isActive(path)
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-secondary text-foreground'
              }`}
            >
              <Icon className="w-6 h-6" />
              <span className="font-medium">{label}</span>
            </NavLink>
          ))}
        </nav>
        
        {/* User Section */}
        <div className="border-t border-border/50 pt-4 mt-4">
          <NavLink
            to={`/profile/${user?.username}`}
            className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-colors ${
              location.pathname.includes('/profile') ? 'bg-secondary' : 'hover:bg-secondary'
            }`}
            data-testid="nav-profile"
          >
            <Avatar className="w-10 h-10 ring-2 ring-primary/20">
              <AvatarImage src={user?.avatar} alt={user?.display_name} />
              <AvatarFallback className="bg-primary/10 text-primary">
                {getInitials(user?.display_name || user?.username)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{user?.display_name || user?.username}</p>
              <p className="text-sm text-muted-foreground truncate">@{user?.username}</p>
            </div>
          </NavLink>
          
          <NavLink
            to="/settings"
            className="flex items-center gap-4 px-4 py-3 rounded-2xl hover:bg-secondary transition-colors mt-2"
            data-testid="nav-settings"
          >
            <Settings className="w-6 h-6" />
            <span className="font-medium">{tr('settings')}</span>
          </NavLink>
          
          {/* Theme Toggle - Desktop */}
          <button
            onClick={toggleTheme}
            className="flex items-center gap-4 px-4 py-3 rounded-2xl hover:bg-secondary transition-colors w-full mt-2"
            data-testid="theme-toggle-desktop"
          >
            {theme === 'dark' ? (
              <>
                <Sun className="w-6 h-6 text-yellow-500" />
                <span className="font-medium">Light Mode</span>
              </>
            ) : (
              <>
                <Moon className="w-6 h-6 text-blue-500" />
                <span className="font-medium">Dark Mode</span>
              </>
            )}
          </button>
          
          <button
            onClick={logout}
            className="flex items-center gap-4 px-4 py-3 rounded-2xl hover:bg-destructive/10 text-destructive transition-colors w-full mt-2"
            data-testid="logout-btn"
          >
            <LogOut className="w-6 h-6" />
            <span className="font-medium">{tr('logout')}</span>
          </button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="lg:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0">
        <Outlet context={{ setShowCreatePost, fetchUnreadCount }} />
      </main>

      {/* Mobile Header with Hamburger Menu */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-background/90 backdrop-blur-xl border-b border-border/50 flex items-center justify-between px-4 z-50">
        <button
          onClick={() => setShowMobileMenu(true)}
          className="p-2 rounded-full hover:bg-secondary"
          data-testid="hamburger-menu-btn"
        >
          <Menu className="w-6 h-6" />
        </button>
        
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="FityH" className="w-8 h-8 object-contain" />
          <span className="text-lg font-bold text-primary">FityH</span>
        </div>
        
        <button 
          onClick={() => setShowSearch(true)}
          className="p-2 rounded-full hover:bg-secondary"
          data-testid="mobile-search-btn"
        >
          <Search className="w-6 h-6" />
        </button>
      </header>

      {/* Mobile Search Modal */}
      {showSearch && (
        <div className="lg:hidden fixed inset-0 z-[100] bg-background/95 backdrop-blur-xl">
          <div className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={() => setShowSearch(false)}
                className="p-2 rounded-full hover:bg-secondary"
              >
                <X className="w-6 h-6" />
              </button>
              <span className="text-lg font-semibold">Search</span>
            </div>
            <GlobalSearchBar onClose={() => setShowSearch(false)} />
          </div>
        </div>
      )}

      {/* Mobile Slide-out Menu */}
      {showMobileMenu && (
        <div className="lg:hidden fixed inset-0 z-[100]">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowMobileMenu(false)}
          />
          
          {/* Menu Panel */}
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-background border-r border-border/50 flex flex-col animate-slide-in-left">
            {/* Header */}
            <div className="p-4 border-b border-border/50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={LOGO_URL} alt="FityH" className="w-10 h-10 object-contain" />
                <div>
                  <h1 className="text-xl font-bold text-primary">FityH</h1>
                  <p className="text-xs text-muted-foreground">{tr('tagline')}</p>
                </div>
              </div>
              <button
                onClick={() => setShowMobileMenu(false)}
                className="p-2 rounded-full hover:bg-secondary"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* User Profile Section */}
            <div className="p-4 border-b border-border/50">
              <NavLink 
                to={`/profile/${user?.username}`}
                onClick={() => setShowMobileMenu(false)}
                className="flex items-center gap-3"
              >
                <Avatar className="w-12 h-12">
                  <AvatarImage src={user?.avatar} alt={user?.display_name} />
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {getInitials(user?.display_name || user?.username)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">{user?.display_name || user?.username}</p>
                  <p className="text-sm text-muted-foreground">@{user?.username}</p>
                </div>
              </NavLink>
            </div>
            
            {/* Menu Items */}
            <div className="flex-1 overflow-y-auto p-2">
              {/* Main Navigation */}
              <div className="space-y-1">
                {navItems.map(({ path, icon: Icon, label, badge }) => (
                  <NavLink
                    key={path}
                    to={path}
                    onClick={() => setShowMobileMenu(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                      isActive(path)
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-secondary text-foreground'
                    }`}
                  >
                    <div className="relative">
                      <Icon className="w-5 h-5" />
                      {badge > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center">
                          {badge > 9 ? '9+' : badge}
                        </span>
                      )}
                    </div>
                    <span className="font-medium">{label}</span>
                  </NavLink>
                ))}
              </div>
              
              {/* Divider */}
              <div className="my-3 border-t border-border/50" />
              
              {/* Extra Features */}
              <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase">Features</p>
              <div className="space-y-1">
                {extraNavItems.map(({ path, icon: Icon, label }) => (
                  <NavLink
                    key={path}
                    to={path}
                    onClick={() => setShowMobileMenu(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                      isActive(path)
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-secondary text-foreground'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{label}</span>
                  </NavLink>
                ))}
              </div>
              
              {/* Divider */}
              <div className="my-3 border-t border-border/50" />
              
              {/* Settings & Info */}
              <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase">Settings & Info</p>
              <div className="space-y-1">
                <NavLink
                  to="/settings"
                  onClick={() => setShowMobileMenu(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                    isActive('/settings')
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-secondary text-foreground'
                  }`}
                >
                  <Settings className="w-5 h-5" />
                  <span className="font-medium">{tr('settings')}</span>
                </NavLink>
                
                <NavLink
                  to="/rules"
                  onClick={() => setShowMobileMenu(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                    isActive('/rules')
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-secondary text-foreground'
                  }`}
                >
                  <Shield className="w-5 h-5" />
                  <span className="font-medium">Rules & Guidelines</span>
                </NavLink>
                
                <NavLink
                  to="/support"
                  onClick={() => setShowMobileMenu(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                    isActive('/support')
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-secondary text-foreground'
                  }`}
                >
                  <HelpCircle className="w-5 h-5" />
                  <span className="font-medium">Help & Support</span>
                </NavLink>
              </div>
            </div>
            
            {/* Logout Button */}
            <div className="p-4 border-t border-border/50">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-secondary transition-colors w-full mb-2"
                data-testid="theme-toggle-btn"
              >
                {theme === 'dark' ? (
                  <>
                    <Sun className="w-5 h-5 text-yellow-500" />
                    <span className="font-medium">Light Mode</span>
                  </>
                ) : (
                  <>
                    <Moon className="w-5 h-5 text-blue-500" />
                    <span className="font-medium">Dark Mode</span>
                  </>
                )}
              </button>
              
              <button
                onClick={() => {
                  setShowMobileMenu(false);
                  logout();
                }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-destructive/10 text-destructive transition-colors w-full"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">{tr('logout')}</span>
              </button>
              
              <p className="text-center text-xs text-muted-foreground mt-4">
                FityH v1.0.0 • © 2026
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 h-16 bg-background/90 backdrop-blur-xl border-t border-border/50 flex justify-around items-center z-50 safe-bottom">
        {[navItems[0], navItems[2]].map(({ path, icon: Icon, label, badge }) => (
          <NavLink
            key={path}
            to={path}
            className={`flex flex-col items-center justify-center w-14 h-full relative ${
              isActive(path) ? 'text-primary' : 'text-muted-foreground'
            }`}
            data-testid={`mobile-nav-${label.toLowerCase().replace(' ', '-')}`}
          >
            <div className="relative">
              <Icon className="w-5 h-5" />
              {badge > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center">
                  {badge > 9 ? '9+' : badge}
                </span>
              )}
            </div>
            <span className="text-[10px] mt-1">{label}</span>
          </NavLink>
        ))}
        
        {/* AI Bot Button in center */}
        <button
          onClick={() => setShowAIBot(true)}
          className="flex flex-col items-center justify-center w-14 h-full text-pink-500"
          data-testid="mobile-nav-ai-bot"
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-violet-500 flex items-center justify-center -mt-4 shadow-lg">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <span className="text-[10px] mt-1">AI Bot</span>
        </button>
        
        {[navItems[4], navItems[5]].map(({ path, icon: Icon, label, badge }) => (
          <NavLink
            key={path}
            to={path}
            className={`flex flex-col items-center justify-center w-14 h-full relative ${
              isActive(path) ? 'text-primary' : 'text-muted-foreground'
            }`}
            data-testid={`mobile-nav-${label.toLowerCase().replace(' ', '-')}`}
          >
            <div className="relative">
              <Icon className="w-5 h-5" />
              {badge > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center">
                  {badge > 9 ? '9+' : badge}
                </span>
              )}
            </div>
            <span className="text-[10px] mt-1">{label}</span>
          </NavLink>
        ))}
        
        <NavLink
          to={`/profile/${user?.username}`}
          className={`flex flex-col items-center justify-center w-14 h-full ${
            location.pathname.includes('/profile') ? 'text-primary' : 'text-muted-foreground'
          }`}
          data-testid="mobile-nav-profile"
        >
          <Avatar className="w-5 h-5">
            <AvatarImage src={user?.avatar} alt={user?.display_name} />
            <AvatarFallback className="text-[8px] bg-primary/10 text-primary">
              {getInitials(user?.display_name || user?.username)}
            </AvatarFallback>
          </Avatar>
          <span className="text-[10px] mt-1">{tr('profile')}</span>
        </NavLink>
      </nav>
      
      {/* Mobile Create Post FAB - Hide on chat pages */}
      {!location.pathname.startsWith('/chat') && (
        <Button
          onClick={() => setShowCreatePost(true)}
          className="lg:hidden fixed right-4 bottom-24 w-14 h-14 rounded-full shadow-lg shadow-primary/25 z-50"
          data-testid="mobile-create-post-btn"
        >
          <Plus className="w-6 h-6" />
        </Button>
      )}
      
      {/* Create Post Modal */}
      <CreatePostModal 
        open={showCreatePost} 
        onClose={() => setShowCreatePost(false)} 
      />
      
      {/* AI Bot Modal */}
      <AIBotModal
        open={showAIBot}
        onClose={() => setShowAIBot(false)}
      />

      {/* Incoming Call Screen — full-screen with ringtone + vibrate */}
      <IncomingCallScreen
        call={incomingCall}
        onAccept={answerCall}
        onDecline={declineCall}
      />
      
      {/* Push Notification Prompt */}
      <NotificationPrompt />
    </div>
  );
}
